import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAppConfig } from "@/contexts/AppConfigContext";
import { LogOut, ShieldCheck, User as UserIcon, UserCog, Home as HomeIcon, ExternalLink } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import acornSrc from "@/assets/brand/acorn.png";

const ROLE_LABEL = {
    pubblico: "Cliente",
    trade: "Trade",
    vip: "VIP",
    staff: "Staff",
    admin: "Amministratore",
};

/**
 * Header — mobile-first, semplificato (Fase 7.1).
 *
 * Gerarchia:
 *  - Logo ghianda + "orterie"
 *  - Navigazione principale: "Vini" (compatta su mobile) · "Distillati" · "Casa Orterie" (link esterno)
 *  - Se utente loggato: menu a tendina con Account/Admin/Esci
 *  - Accedi/Registrati NON in header (spostati nel footer, sono voci secondarie)
 */
export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { publicCfg } = useAppConfig();
    const restaurantUrl = publicCfg?.restaurant_site_url || "";

    return (
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md co-hairline">
            <div className="co-container flex items-center justify-between h-14 sm:h-20 gap-2">
                <Link to="/" className="group flex items-center gap-1.5 sm:gap-3 shrink-0 min-w-0" data-testid="header-logo-link">
                    <img
                        src={acornSrc}
                        alt=""
                        aria-hidden="true"
                        className="h-7 sm:h-11 w-auto shrink-0"
                        style={{ opacity: 0.85 }}
                    />
                    <div className="flex flex-col leading-none min-w-0">
                        {/* Fase 7.1.1: logo più piccolo e Futura Light (peso 200)
                            per far stare header su UNA riga anche a 360px. */}
                        <span
                            className="text-lg sm:text-3xl text-[#212121] tracking-normal truncate"
                            style={{
                                fontFamily: "'Futura', 'Futura PT', 'Futura Std', 'Century Gothic', 'Trebuchet MS', 'Nunito Sans', sans-serif",
                                fontWeight: 200,
                                letterSpacing: "0.01em",
                                textTransform: "lowercase",
                            }}
                            data-testid="header-logo-text"
                        >
                            orterie
                        </span>
                        <span className="hidden sm:inline co-caption mt-1 text-[10px]">
                            Carta dei Vini
                        </span>
                    </div>
                </Link>

                <nav className="flex items-center gap-2 sm:gap-5 shrink-0" data-testid="header-nav">
                    <Link
                        to="/distillati"
                        data-testid="header-spirits-link"
                        className="text-sm sm:text-base text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))] transition-colors whitespace-nowrap"
                    >
                        Distillati
                    </Link>
                    {restaurantUrl && (
                        <a
                            href={restaurantUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-restaurant-link"
                            className="hidden md:inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors whitespace-nowrap"
                            title="Vai al sito di Casa Orterie"
                        >
                            <HomeIcon className="w-4 h-4" strokeWidth={1.5} />
                            Casa Orterie
                            <ExternalLink className="w-3 h-3 opacity-60" strokeWidth={1.5} />
                        </a>
                    )}

                    {user && (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                data-testid="header-user-menu-trigger"
                                className="flex items-center gap-1.5 sm:gap-2 rounded-sm border border-[hsl(var(--border))] px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm hover:bg-[hsl(var(--secondary))] transition-colors"
                            >
                                <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                                <span className="hidden lg:inline">{user.email}</span>
                                <span className="co-badge-neutral hidden sm:inline">{ROLE_LABEL[user.role] || user.role}</span>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="font-normal">
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Autenticato come</div>
                                    <div className="text-sm truncate">{user.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {restaurantUrl && (
                                    <DropdownMenuItem asChild data-testid="header-menu-restaurant-link" className="cursor-pointer">
                                        <a href={restaurantUrl} target="_blank" rel="noopener noreferrer">
                                            <HomeIcon className="w-4 h-4 mr-2" />
                                            Sito Casa Orterie
                                            <ExternalLink className="w-3 h-3 ml-auto opacity-60" strokeWidth={1.5} />
                                        </a>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={() => navigate("/account")}
                                    data-testid="header-menu-account-link"
                                    className="cursor-pointer"
                                >
                                    <UserCog className="w-4 h-4 mr-2" />
                                    Il mio account
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem
                                        onClick={() => navigate("/admin/vini")}
                                        data-testid="header-menu-admin-link"
                                        className="cursor-pointer"
                                    >
                                        <ShieldCheck className="w-4 h-4 mr-2" />
                                        Pannello amministrazione
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={() => {
                                        logout();
                                        navigate("/");
                                    }}
                                    data-testid="header-menu-logout"
                                    className="cursor-pointer"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Esci
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </nav>
            </div>
        </header>
    );
}
